import { useEffect, useRef, useState } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { HiOutlineTrash } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import { UserData } from '../../services/userService';
import {
  UserAssignment, fetchUserAssignments, assignUserToParent, unassignUserFromParent,
} from '../../services/userAssignmentService';
import { fetchTrackableParents, TrackableParent } from '../../services/customerHealthService';
import './UserAssignmentsModal.css';

interface UserAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser: UserData | null;
}

const SEARCH_LIMIT = 10;

/**
 * Administrator/executive-only: assign/unassign the ultimate parents a user can access
 * (`app.user_assignment`), driving `business_dev`/`customer_success` row-level scoping on
 * Customer Health (see app_customer_health.yaml's `assignments.user_id` filter). Mirrors the
 * "Add Ultimate Parent" picker on Customer Health (CustomerHealth.tsx's `addParent` modal) —
 * same debounced server search via `fetchTrackableParents`, same on_conflict-safe assign.
 */
export default function UserAssignmentsModal({ isOpen, onClose, targetUser }: UserAssignmentsModalProps) {
  const [assignments, setAssignments] = useState<UserAssignment[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TrackableParent[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [assigningParent, setAssigningParent] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const searchReqId = useRef(0);

  const userId = targetUser?.id;

  const loadAssignments = () => {
    if (!userId) return;
    setLoadError(null);
    fetchUserAssignments(userId)
      .then(setAssignments)
      .catch(() => setLoadError('Failed to load current assignments.'));
  };

  useEffect(() => {
    if (!isOpen || !userId) return;
    setAssignments(null);
    setQuery('');
    setSearchResults(null);
    setAssignError(null);
    loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  // Debounced server search, excluding parents already assigned to this user.
  useEffect(() => {
    if (!isOpen || assignments === null) return;
    const assignedNames = assignments.map((a) => a.ultimate_parent);
    const reqId = ++searchReqId.current;
    setSearching(true);
    const handle = setTimeout(() => {
      fetchTrackableParents(query, assignedNames, SEARCH_LIMIT)
        .then((parents) => { if (searchReqId.current === reqId) setSearchResults(parents); })
        .catch(() => { if (searchReqId.current === reqId) setSearchResults([]); })
        .finally(() => { if (searchReqId.current === reqId) setSearching(false); });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, query, assignments]);

  if (!isOpen || !targetUser) return null;

  const handleAssign = async (ultimateParent: string) => {
    if (!userId || assigningParent) return;
    setAssignError(null);
    setAssigningParent(ultimateParent);
    try {
      const row = await assignUserToParent(userId, ultimateParent);
      setAssignments((prev) => [row, ...(prev || [])]);
    } catch (err: any) {
      const message = err?.response?.errors?.[0]?.message;
      setAssignError(message || 'Failed to assign this ultimate parent. You may not have permission.');
    } finally {
      setAssigningParent(null);
    }
  };

  const handleUnassign = async (assignment: UserAssignment) => {
    if (removingId) return;
    if (!window.confirm(`Remove ${targetUser.name || targetUser.email}'s access to "${assignment.ultimate_parent}"?`)) return;
    setRemovingId(assignment.id);
    try {
      await unassignUserFromParent(assignment.id);
      setAssignments((prev) => (prev || []).filter((a) => a.id !== assignment.id));
    } catch (err) {
      toast.error('Failed to remove this assignment.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const results = searchResults || [];

  return (
    <div className="assignments-dialog-overlay" onClick={handleOverlayClick}>
      <div className="assignments-dialog">
        <div className="assignments-dialog-header">
          <div>
            <h2>Manage Parent Access</h2>
            <p className="assignments-dialog-subtitle">{targetUser.name || targetUser.email}</p>
          </div>
          <button onClick={onClose} className="assignments-close-btn">
            <HiOutlineX />
          </button>
        </div>

        <div className="assignments-dialog-body">
          <div className="assignments-section">
            <label className="assignments-section-label">Assigned Ultimate Parents</label>
            {loadError ? (
              <p className="assignments-error">{loadError}</p>
            ) : assignments === null ? (
              <p className="assignments-empty">Loading…</p>
            ) : assignments.length === 0 ? (
              <p className="assignments-empty">No ultimate parents assigned yet.</p>
            ) : (
              <div className="assignments-tag-list">
                {assignments.map((a) => (
                  <div key={a.id} className="assignment-tag">
                    <span className="assignment-tag-text">{a.ultimate_parent}</span>
                    <button
                      className="assignment-tag-remove"
                      onClick={() => handleUnassign(a)}
                      disabled={removingId === a.id}
                      title="Remove access"
                      type="button"
                    >
                      <HiOutlineTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="assignments-section">
            <label className="assignments-section-label">Assign an Ultimate Parent</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ultimate parents…"
              className="assignments-search-input"
              autoFocus
            />
            {assignError && <p className="assignments-error">{assignError}</p>}
            <div className="assignments-results-list">
              {assignments === null ? null : results.length ? (
                results.map((p) => (
                  <button
                    key={p.ultimate_parent}
                    className="assignment-result-item"
                    onClick={() => handleAssign(p.ultimate_parent)}
                    disabled={!!assigningParent}
                    type="button"
                  >
                    <div>
                      <div className="assignment-result-name">{p.ultimate_parent}</div>
                      <div className="assignment-result-meta">
                        {p.stores.toLocaleString()} Stores · {p.brands} {p.brands === 1 ? 'Brand' : 'Brands'}
                      </div>
                    </div>
                    <span className="assignment-result-action">
                      {assigningParent === p.ultimate_parent ? 'Assigning…' : 'Assign'}
                    </span>
                  </button>
                ))
              ) : (
                <p className="assignments-empty">
                  {searching ? 'Searching…' : query.trim() ? `No ultimate parents match "${query.trim()}".` : 'All matching ultimate parents are already assigned.'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="assignments-dialog-footer">
          <button onClick={onClose} className="assignments-btn-close" type="button">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
